import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { SxProps, Theme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ForwardedRef } from 'react';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import Stack from '@mui/material/Stack';
import Chip, { ChipVariantEnum } from 'components/Chips/Chip';

export const UnClickableStyleEnum = Object.freeze({
  DIM_BACKGROUND: 'DIM_BACKGROUND',
  DEFAULT_BACKGROUND: 'DEFAULT_BACKGROUND',
  CLICKABLE: 'CLICKABLE',
});

const useStyles = makeStyles()((theme) => ({
  unClick: {
    display: 'block',
    maxWidth: 5150,
    minWidth: 200,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(2)}`,
    margin: `${theme.spacing(1)} 0`,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.border.main}`,
    transition: '300ms',
    background: theme.palette.background.default,
    cursor: 'default',
  },
  unClickDefault: {
    display: 'block',
    maxWidth: 5150,
    minWidth: 200,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(2)}`,
    margin: `${theme.spacing(1)} 0`,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.border.main}`,
    transition: '300ms',
    background: theme.palette.background.paper,
    cursor: 'default',
  },
  button: {
    display: 'block',
    maxWidth: 5150,
    minWidth: 200,
    padding: `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(2)}`,
    margin: `${theme.spacing(1)} 0`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    transition: '300ms',
    background: theme.palette.background.paper,
    '&:hover': {
      boxShadow: theme.shadows[5],
    },
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  disabled: {
    opacity: 0.5,
  },
  mainText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

interface QPanelButtonProps {
  unClickable?: string;
  arrow?: React.ReactNode;
  centerText?: boolean;
  textMarginLeft?: number;
  textMarginRight?: number;
  hideArrow?: boolean;
  className?: string;
  onClick?: () => void;
  startIcon?: React.ReactNode;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  id?: string;
  tabIndex?: number;
  disabled?: boolean;
  chip?: string;
  sx?: SxProps<Theme>;
  [key: string]: unknown;
}

const QPanelButton = React.forwardRef(
  (
    {
      title,
      subtitle,
      startIcon,
      hideArrow,
      className,
      onClick,
      textMarginLeft,
      textMarginRight,
      centerText,
      arrow,
      unClickable,
      chip,
      ...otherProps
    }: QPanelButtonProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { classes, cx } = useStyles();
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (onClick && e.keyCode === 13) onClick();
    };

    const buttonContent = (
      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' height='100%'>
        <Box display='flex'>
          {startIcon}
          <Box
            ml={typeof textMarginLeft === 'number' ? textMarginLeft : 2}
            mr={typeof textMarginRight === 'number' ? textMarginRight : 3}
            display='flex'
            flexDirection='column'
            justifyContent={centerText ? 'center' : 'space-between'}
            gap={1}
          >
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='bodyRegular' className={classes.mainText}>
                {title}
              </Typography>
              {chip ? <Chip variant={ChipVariantEnum.UPCOMING}>{chip}</Chip> : null}
            </Stack>
            {subtitle && (
              <Typography className={classes.secondaryText} variant='paragraphRegular'>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {!hideArrow && !arrow && (
          <IconButton size='large'>
            <ArrowForwardIosRoundedIcon fontSize='small' />
          </IconButton>
        )}
        {arrow && arrow}
      </Box>
    );

    if (
      unClickable === UnClickableStyleEnum.DIM_BACKGROUND ||
      unClickable === UnClickableStyleEnum.DEFAULT_BACKGROUND
    ) {
      return (
        <div
          className={cx(
            unClickable === UnClickableStyleEnum.DIM_BACKGROUND ? classes.unClick : classes.unClickDefault,
            className,
          )}
          {...otherProps}
        >
          {buttonContent}
        </div>
      );
    }

    return (
      <ButtonBase
        component={Paper}
        elevation={4}
        onKeyDown={handleKeyDown}
        onClick={onClick}
        classes={{ disabled: classes.disabled }}
        {...otherProps}
        ref={ref}
        className={cx(classes.button, className)}
        sharedcomponentid='QPanelButton'
      >
        {buttonContent}
      </ButtonBase>
    );
  },
);

QPanelButton.displayName = 'QPanelButton';

export default QPanelButton;
